import get from 'lodash/get'
import { GET_APPRAISAL_RECORD_DETAIL, GET_APPRAISAL_ASSIGN_DETAIL } from '~/graphql/queries/performance'
import { AppraisalRecordType } from '~/global/data/performance'
import { GET_USER_ACTIVITY_HISTORIES } from '~/graphql/queries/users'

import { USER_ACTIVITY_TARGET_TYPE } from '~/utils/const'
import { APPRAISAL_SAVE_REASON_FOR_UPDATE } from '~/graphql/mutations/performance'

const mutationTypes = {
  SET_APPRAISAL_RECORD_ONFOCUS: 'SET_APPRAISAL_RECORD_ONFOCUS',
  SET_APPRAISAL_RECORD_REMAIN_ONFOCUS: 'SET_APPRAISAL_RECORD_REMAIN_ONFOCUS',
  SET_APPRAISAL_ASSIGN_ACTIVITIES: 'SET_APPRAISAL_ASSIGN_ACTIVITIES',
}

const actionTypes = {
  FETCH_APPRAISAL_RECORD_DETAIL: 'FETCH_APPRAISAL_RECORD_DETAIL',
  FETCH_APPRAISAL_ASSIGN_DETAIL: 'FETCH_APPRAISAL_ASSIGN_DETAIL',
  APPRAISAL_SAVE_REASON_FOR_UPDATE: 'APPRAISAL_SAVE_REASON_FOR_UPDATE',
  FETCH_APPRAISAL_ASSIGN_ACTIVITIES: 'FETCH_APPRAISAL_ASSIGN_ACTIVITIES',
}

export const state = () => ({
  appraisalRecordOnFocus: {},
  appraisalRecordRemainOnFocus: {},
  appraisalAssignActivities: {
    data: [],
    meta: {
      limit: 5,
      total: 0,
      page: 1,
    },
    loading: false,
  },
})

export const mutations = {
  [mutationTypes.SET_APPRAISAL_RECORD_ONFOCUS](state, payload) {
    state.appraisalRecordOnFocus = payload
  },
  [mutationTypes.SET_APPRAISAL_RECORD_REMAIN_ONFOCUS](state, payload) {
    state.appraisalRecordRemainOnFocus = payload
  },
  [mutationTypes.SET_APPRAISAL_ASSIGN_ACTIVITIES](state, payload) {
    state.appraisalAssignActivities = payload
  },
}

export const actions = {
  async [actionTypes.FETCH_APPRAISAL_ASSIGN_DETAIL]({ commit }, params) {
    try {
      const { appraisalAssignId, personalProfileId } = params
      const apolloClient = this.app.apolloProvider.defaultClient
      const { data } = await apolloClient.query({
        query: GET_APPRAISAL_ASSIGN_DETAIL,
        fetchPolicy: 'network-only',
        variables: {
          id: appraisalAssignId,
        },
      })
      if (data && data.appraisalAssign) {
        const { records } = data.appraisalAssign
        const foundRecordIndex = records.findIndex((record) => {
          if (!personalProfileId) return record.type === AppraisalRecordType.MANAGER
          return record.personalProfileId === personalProfileId
        })
        commit(mutationTypes.SET_APPRAISAL_RECORD_ONFOCUS, records[foundRecordIndex])
        commit(mutationTypes.SET_APPRAISAL_RECORD_REMAIN_ONFOCUS, records[foundRecordIndex === 1 ? 0 : 1])
      }
    } catch (error) {
      console.error('FETCH_APPRAISAL_ASSIGN_DETAIL', error)
    }
  },
  async [actionTypes.FETCH_APPRAISAL_RECORD_DETAIL]({ commit }, params) {
    try {
      const { isRemain, personalProfileId, appraisalRecordId } = params
      const apolloClient = this.app.apolloProvider.defaultClient
      const { data } = await apolloClient.query({
        query: GET_APPRAISAL_RECORD_DETAIL,
        fetchPolicy: 'network-only',
        variables: {
          personalProfileId,
          appraisalRecordId,
        },
      })
      if (data && data.appraisalRecord) {
        commit(
          mutationTypes[isRemain ? 'SET_APPRAISAL_RECORD_REMAIN_ONFOCUS' : 'SET_APPRAISAL_RECORD_ONFOCUS'],
          data.appraisalRecord
        )
        return
      }
      throw data
    } catch (error) {
      console.error('FETCH_APPRAISAL_RECORD_DETAIL', error)
    }
  },
  async [actionTypes.APPRAISAL_SAVE_REASON_FOR_UPDATE](_, { appraisalAssignId, reason }) {
    const apolloClient = this.app.apolloProvider.defaultClient
    try {
      const { data } = await apolloClient.mutate({
        mutation: APPRAISAL_SAVE_REASON_FOR_UPDATE,
        variables: {
          appraisalAssignId,
          activityStatus: 'APPRAISAL_SUBMIT_FINALIZED',
          reason,
        },
      })
      return {
        data,
        success: true,
      }
    } catch (error) {
      return {
        error,
        success: false,
      }
    }
  },
  async [actionTypes.FETCH_APPRAISAL_ASSIGN_ACTIVITIES]({ getters, commit }, { schoolId, page, limit, targetId }) {
    commit(mutationTypes.SET_APPRAISAL_ASSIGN_ACTIVITIES, { ...getters.appraisalAssignActivities, loading: true })
    const apolloClient = this.app.apolloProvider.defaultClient
    try {
      const { data } = await apolloClient.query({
        query: GET_USER_ACTIVITY_HISTORIES,
        fetchPolicy: 'network-only',
        variables: {
          targetType: USER_ACTIVITY_TARGET_TYPE.APPRAISAL_ASSIGN,
          schoolId,
          targetId,
          page,
          limit,
        },
      })
      if (data && data.userActivityHistories) {
        commit(mutationTypes.SET_APPRAISAL_ASSIGN_ACTIVITIES, data.userActivityHistories)
      }
    } catch (error) {
      console.error('FETCH_APPRAISAL_ASSIGN_ACTIVITIES', error)
    } finally {
      commit(mutationTypes.SET_APPRAISAL_ASSIGN_ACTIVITIES, { ...getters.appraisalAssignActivities, loading: false })
    }
  },
}

export const getters = {
  appraisalRecordOnFocus: (state) => state.appraisalRecordOnFocus,
  appraisalRecordRemainOnFocus: (state) => state.appraisalRecordRemainOnFocus,
  appraisalRecordType: (state) => get(state.appraisalRecordOnFocus, 'type', null),
  appraisalRecordStatus: (state) => get(state.appraisalRecordOnFocus, 'status', null),
  appraisalRecordRemainStatus: (state) => get(state.appraisalRecordRemainOnFocus, 'status', null),
  appraisalRecordTypeRemain: (state) => get(state.appraisalRecordRemainOnFocus, 'type', null),
  getAppraiserByType: (state) => (typeToFind) => {
    const type = get(state.appraisalRecordOnFocus, 'type', null)
    return type === typeToFind
      ? get(state.appraisalRecordOnFocus, 'personalProfile', {})
      : get(state.appraisalRecordRemainOnFocus, 'personalProfile', {})
  },
  getRecordStatusByType: (state) => (type) =>
    get(
      get(state.appraisalRecordOnFocus, 'appraisalAssign.records', []).find((record) => record.type === type),
      'status',
      ''
    ),
  appraisalAssignActivities: (state) => state.appraisalAssignActivities,
}
